import cn from "classnames";
import Link from "next/link";
import { useRef } from "react";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { ProviderHooks } from "@/src/hook";
import { FrontendTrackingService } from "@/src/service";
import { NestedCarouselItem } from "@/src/type/strapi/component/section/NestedCarousel";
import { LoadFromCDN } from "@/src/util";

import TextModule from "../../component/TextModule/TextModule";

import styles from "./NestedCarouselBaseItem.module.scss";

interface NestedCarouselBaseItemProps {
  item: NestedCarouselItem;
  onClick?: (ctaClick?: () => void) => void;
  isOutline?: boolean;
  lazy?: "lazy" | "eager";
  quality?: number;
}

function NavigationCarouselBaseItem({
  item: { id, staticBackground, title, subtitle, cta },
  onClick,
  isOutline,
  lazy,
  quality,
}: NestedCarouselBaseItemProps) {
  const cms = ProviderHooks.useCMS();
  const ctaLinkRef = useRef<HTMLAnchorElement>(null);

  const handleContainerClick = () => {
    FrontendTrackingService.postTrackingLog({
      action: "click",
      actionId: `NestedCarouselItem:${id}`,
      actionIdType: "componentName:componentId",
      actionMessage: `NestedCarouselItem clicked with ${title}`,
      triggeredFrom: "NavigationCarouselBaseItem.tsx:(handleContainerClick)",
    });

    const ctaTargetRef = ctaLinkRef.current;

    if (onClick) {
      onClick?.(ctaTargetRef ? () => ctaTargetRef.click() : undefined);
      return;
    }

    if (ctaLinkRef.current) {
      ctaLinkRef.current.click();
    }
  };

  const getHref = (href: string, isExternal: boolean) => {
    return isExternal ? href : cms.constructLink(href);
  };

  return (
    <div
      className={cn(isOutline ? styles.baseItemOutline : styles.baseItem)}
      onClick={handleContainerClick}
    >
      <ImgBgResponsive
        className={styles.baseItemImage}
        imageUrl={LoadFromCDN.imageQualityAndSizeOptional(staticBackground.data.attributes.url, quality)}
        triggers={["swiperSlideChange", "click", "touchstart", "scroll"]}
        prefetch={lazy === "eager"}
        lazy={lazy ?? "lazy"}
      />
      <div className={styles.baseItemContent}>
        <TextModule
          text={{
            tag: "body1",
            text: title,
          }}
          className={styles.baseItemTitle}
        />
        <TextModule
          text={{
            tag: "body1",
            text: subtitle,
          }}
          className={styles.baseItemSubtitle}
        />
      </div>
      {!!cta && (
        <Link
          ref={ctaLinkRef}
          href={getHref(cta.href, cta.isExternal)}
          target={cta?.target}
          hidden={true}
        >
          {cta.label}
        </Link>
      )}
    </div>
  );
}

export default NavigationCarouselBaseItem;
